import { inject, Injectable } from '@angular/core';

import { StoreService } from '../stores/store.service';
import { LanguageService } from './languages.service';
import { LoginAPIService } from './login-api.service';
import { MobileService } from './mobile.service';
import { NavigationService } from './navigation.service';

import { TvLoginStore } from '../../pages/app/services/app.store';
import { ErrorResponse } from '../entities/error-response';
import { LoginRequest } from '../entities/login-request';
import { LoginResponse } from '../entities/login-response';
import { DictionaryAPIService } from './dictionary-api.service';

@Injectable()
export class LoginCommonService {
  readonly tvLoginStore = inject(TvLoginStore);

  constructor(
    private loginAPIService: LoginAPIService,
    private mobileService: MobileService,
    private navigationService: NavigationService,
    private languageService: LanguageService,
    private storeService: StoreService,
    private dictionaryAPIService: DictionaryAPIService
  ) {}

  login(model: LoginRequest): Promise<any> {
    return this.loginAPIService
      .login(model)
      .then((response) => new LoginResponse(response))
      .then(this.doCSRFTokenRequest())
      .then((loginResponse: LoginResponse) => {
        return this.loginWithLoginResponse(loginResponse, model);
      })
      .catch((response) => {
        throw new ErrorResponse(response.error);
      });
  }

  public doCSRFTokenRequest() {
    return (loginResponse: LoginResponse) => {
      if (!loginResponse) {
        return this.loginAPIService.setCSRFToken().then(() => loginResponse);
      }
      if (loginResponse && loginResponse.userId && !this.hasTokenInResponse(loginResponse)) {
        return this.loginAPIService.setCSRFToken().then(() => loginResponse);
      }

      return loginResponse;
    };
  }

  loginWithLoginResponse(loginResponse: LoginResponse, model): Promise<any> {
    /**
     * to check this case on web - use url with param '/mobile'
     * @Example
     * https://localhost:44319/login/mobile
     */
    this.storeService.loginResponse = loginResponse;
    this.storeService.loginResponse.userEmail = model.login;
    this.setCurrentCorporateIdCookie(loginResponse);
    this.navigationService.putClientToStore(loginResponse.client);

    const languageFromStore = this.tvLoginStore.getSelectedLanguageId();
    if (!languageFromStore) {
      const languageIdFromUserPreference = this.storeService.getUserPreferredLanguageId();
      if (languageIdFromUserPreference) {
        const language = this.languageService.findLanguage(languageIdFromUserPreference);
        this.languageService.changeSystemLanguageAndPutToStore(language);
      }
    }

    const needSelectLanguage = !this.languageService.isContainsClientLanguages(loginResponse.client.languageIds);

    if (needSelectLanguage) {
      return this.navigationService.goToTermsPageWithSelectLanguage(loginResponse);
    }

    return this.navigationService.goToTermsAndConditions(loginResponse.clientAlias);
  }

  checkInternetConnection(alias: string) {
    const url =
      this.dictionaryAPIService.getClientSSO(alias) +
      '&isMobile=' +
      (this.mobileService.isMobileApp() ? 'true' : 'false');

    return this.loginAPIService.checkInternetConnection(url);
  }

  private hasTokenInResponse(loginResponse: LoginResponse): boolean {
    return !!(loginResponse && loginResponse.token);
  }

  private setCurrentCorporateIdCookie(loginResponse: LoginResponse) {
    document.cookie = 'CurrentCorporateId=' + loginResponse.client.corporate.id + '; path=/';
  }
}
